/** @jsxImportSource @emotion/react */
import {
  Button,
  Checkbox,
  Card,
  CheckboxGroup,
  Input,
  Select,
  Typography,
  useTheme,
  createStyles,
} from '@quickbit/qb-design-system';
import { useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import {
  RelatedPeople,
  ContactInformationForm,
  Section,
} from 'pages/SignUp/components';
import { COUNTRY_OPTIONS } from '../../../utils';
import { RangeCheckboxes } from '../components/RangeCheckboxes';
import { MerchantContext } from '../SignUp';
import { BUSINESS_SECTOR_OPTIONS, BusinessSector } from '../types';
import { isValidEmail } from '../utils';

interface BusinessInformationProps {
  showTerms: boolean;
}

export const BusinessInformation = ({ showTerms }: BusinessInformationProps ) => {
  const navigate = useNavigate();
  const {
    merchantUseForm,
    kybUseForm,
    email,
    onUpdateQueryParam,
    onSubmit,
    loading,
  } = useOutletContext<MerchantContext>();

  const styles = useStyles();

  const { spacing } = useTheme();
  const { hasErrors: merchantFormHasErrors } = merchantUseForm;
  const {
    fields: kybForm,
    setters: kybFormSetters,
    errors: kybFormErrors,
    hasErrors: kybFormHasErrors,
  } = kybUseForm;

  const invalidEmail = !isValidEmail(email);
  const emptyJurisdictions = kybForm.jurisdictions.length === 0;

  const autofill = () => {
    kybFormSetters.registeredAddress.address1.set('Sveavägen 20');
    kybFormSetters.registeredAddress.country.set('SE');
    kybFormSetters.registeredAddress.postalNumber.set('111 57');
    kybFormSetters.registeredAddress.postalRegion.set('Stockholm');
    kybFormSetters.businessSectors.set(['Retail & E-commerce']);
    kybFormSetters.businessOperations?.set(
      'Quickbit Eu AB (publ) is a licensed payment institution in Sweden, regulated by the Swedish Financial Supervisory Authority (Finansinspektionen).'
    );
    kybFormSetters.reasonForUse?.set('We need Valuno Pay for our clients');
    kybFormSetters.jurisdictions.set([{ label: 'Sweden', value: 'SE' }]);
    kybFormSetters.turnover.set(1000000);
    kybFormSetters.expectedMonthlyVolume.set(1000000);
    kybFormSetters.expectedMonthlyTransactions.set(1000000);
    merchantUseForm.setters.tosAccepted.set(true);
  };

  useEffect(() => {
    if (!showTerms) {
      merchantUseForm.setters.tosAccepted.set(true);
    }
  }, [showTerms, merchantUseForm.setters]);

  return (
    <>
      <Section>
        <ContactInformationForm
          label="Address of registration"
          data={kybForm.registeredAddress}
          editable={true}
          setters={kybFormSetters.registeredAddress}
          errors={kybFormErrors.registeredAddress}
          onBlur={onUpdateQueryParam}
        />
        <Card>
          <div
            css={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: spacing.xs,
            }}
          >
            <Typography variant="bodyBold">
              Select all industries your company operates in:
            </Typography>
          </div>
          <CheckboxGroup
            options={BUSINESS_SECTOR_OPTIONS}
            values={kybForm.businessSectors}
            onChange={(s) => {
              kybFormSetters.businessSectors.set(s as BusinessSector[]);
              onUpdateQueryParam('businessSectors', JSON.stringify(s));
            }}
          />
        </Card>
        <Input
          label="Short description of your company"
          type="textarea"
          rows={3}
          value={kybForm.businessOperations ?? ''}
          onChange={(e) =>
            kybFormSetters.businessOperations?.set(e.target.value)
          }
          onBlur={(e) =>
            onUpdateQueryParam('businessOperations', e.target.value)
          }
        />
        <Input
          label="What is the reason why your company wants to become a customer and use this specific product?"
          type="textarea"
          rows={3}
          value={kybForm.reasonForUse ?? ''}
          onChange={(e) => kybFormSetters.reasonForUse?.set(e.target.value)}
          onBlur={(e) => onUpdateQueryParam('reasonForUse', e.target.value)}
        />
        <Card>
          <div
            css={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: spacing.xs,
            }}
          >
            <Typography variant="bodyBold">
              Politically Exposed Person (PEP) and Relatives and Close
              Associates (RCA)
            </Typography>
          </div>
          <RelatedPeople
            data={kybForm.relatedPeople}
            errors={kybFormErrors.relatedPeople}
            editable={true}
            setters={kybFormSetters.relatedPeople}
            onBlur={onUpdateQueryParam}
          />
        </Card>
        <Card>
          <div
            css={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: spacing.xs,
            }}
          >
            <Typography variant="bodyBold">
              Which countries will you provide Valuno Pay in
            </Typography>
          </div>
          <Select
            multiple={true}
            label="Countries"
            options={COUNTRY_OPTIONS}
            values={kybForm.jurisdictions.map((option) => option.value)}
            onSelect={(value: string[]) => {
              const values = COUNTRY_OPTIONS.filter((countryOption) =>
                value.includes(countryOption.value)
              );
              kybFormSetters.jurisdictions.set(values);
              onUpdateQueryParam('jurisdictions', JSON.stringify(values));
            }}
          />
        </Card>
        <Card>
          <div
            css={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: spacing.xs,
            }}
          >
            <Typography variant="bodyBold">
              What was the company’s turnover (in EUR) last year?
            </Typography>
          </div>
          <RangeCheckboxes
            ranges={[
              ['0', '100,000'],
              ['100,000', '500,000'],
              ['500,000', '1000,000'],
              ['1000,000', '5000,000'],
              ['5000,000', '+'],
            ]}
            onChange={(range) => {
              onUpdateQueryParam('turnover', range);
              kybFormSetters.turnover.set(range);
            }}
            selected={kybForm.turnover}
          />
        </Card>
        <Card>
          <div
            css={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: spacing.xs,
            }}
          >
            <Typography variant="bodyBold">
              How large volume (in EUR) do you estimate you will process with
              Valuno Pay?
            </Typography>
          </div>
          <RangeCheckboxes
            ranges={[
              ['0', '100,000'],
              ['100,000', '500,000'],
              ['500,000', '1000,000'],
              ['1000,000', '5000,000'],
              ['5000,000', '+'],
            ]}
            onChange={(range) => {
              onUpdateQueryParam('expectedMonthlyVolume', range);
              kybFormSetters.expectedMonthlyVolume.set(range);
            }}
            selected={kybForm.expectedMonthlyVolume}
          />
        </Card>
        <Card>
          <div
            css={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: spacing.xs,
            }}
          >
            <Typography variant="bodyBold">
              How many transactions yearly will you process with Valuno Pay?
            </Typography>
          </div>
          <RangeCheckboxes
            ranges={[
              ['0', '100,000'],
              ['100,000', '500,000'],
              ['500,000', '1000,000'],
              ['1000,000', '5000,000'],
              ['5000,000', '+'],
            ]}
            onChange={(range) => {
              onUpdateQueryParam('expectedMonthlyTransactions', range);
              kybFormSetters.expectedMonthlyTransactions.set(range);
            }}
            selected={kybForm.expectedMonthlyTransactions}
          />
        </Card>
      </Section>
      {showTerms && (
        <Section>
          <label>
            <Card css={styles.tosSection}>
              <div
                css={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing.xs,
                }}
              >
                <Checkbox
                  checked={merchantUseForm.fields.tosAccepted}
                  onChange={merchantUseForm.setters.tosAccepted.set}
                />
                <Typography variant="body">
                  I accept the Valuno{' '}
                  <a href="/Terms of Use Valuno Pay.pdf" target="_blank">
                    Terms and conditions
                  </a>
                </Typography>
              </div>
            </Card>
          </label>
        </Section>
      )}
      <Section>
        <div css={{ display: 'flex', justifyContent: 'flex-end' }}>
          <div
            css={{
              display: 'inline-flex',
              justifyContent: 'flex-end',
            }}
          >
            <Button
              text="Prev"
              variant="secondary"
              onClick={() =>
                navigate(
                  `/sign-up/general-information${window.location.search}`
                )
              }
            />
            <div css={{ width: spacing.xs }} />
            <Button
              text="Submit"
              onClick={onSubmit}
              loading={loading}
              disabled={
                invalidEmail ||
                kybFormHasErrors ||
                merchantFormHasErrors ||
                !merchantUseForm.fields.tosAccepted ||
                emptyJurisdictions
              }
            />
            {process.env.REACT_APP_ENVIRONMENT !== 'production' && (
              <Button
                style={{ marginLeft: '8px' }}
                text="autofill"
                variant="primary"
                onClick={autofill}
              />
            )}
          </div>
        </div>
      </Section>
    </>
  );
};

const useStyles = createStyles(({ spacing, palette }) => ({
  tosSection: {
    borderRadius: spacing.xxxs,
    border: `1px solid ${palette.grey.x200}`,
    ':hover': {
      borderColor: palette.grey.x600,
    },
  },
}));
